import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { useAppSelector } from 'redux/store';
import { auth } from '../firebase';
import { identify, trackEvent, TRACK_EVENT, refreshAnalyticId } from '../utils/analytics';
import { FacebookAuthProvider, OAuthProvider, GoogleAuthProvider } from 'firebase/auth';
import UserType from 'constants/user';
import _split from 'lodash/split';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useLazyQuery } from '@apollo/client';
import { CREATE_USER_IF_NOT_EXIST, UPDATE_PROFILE } from 'graph_ql/authentication/mutation';
import { PATH } from 'routes/constant';
import { USER_DETAIL } from 'graph_ql/authentication/queries';
import { loginCallBack, trackSignupEvent } from 'containers/authentication/useAuthenticate';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthToken } from 'hooks/useAuthToken';
import { persistor } from 'redux/store';
import { toast } from 'react-toastify';
import { authenticationActions } from 'redux/authentication/authentication.slice';
import { GET_SEARCH_KEY } from 'graph_ql/search/get_search_key.gql';
import utils from 'utils/utils';
import { LOCAL_STORAGE_KEY } from 'utils/constant';
import { TRANSFORM_LOCAL_USER_DATA } from 'graph_ql/users/mutation.gql';

export const AuthContext = React.createContext();

export function useAuth() {
    return useContext(AuthContext);
}

const AuthProvider = ({ children }) => {
    const methods = useForm();
    let history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const { removeAuthToken } = useAuthToken();
    const [currentUser, setCurrentUser] = useState();
    const currentUserRef = useRef(); // same as currentUser, but can use realtime value
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const [loggedInUserType, setLoggedInUserType] = useState('');
    const [loggedInUserEmail, setLoggedInUserEmail] = useState('');
    const [loggedInUserGender, setLoggedInUserGender] = useState('');
    const [loading, setLoading] = useState(true);
    const [socialProviderInfo, setSocialProviderInfo] = useState({});

    const userType = useAppSelector((state) => state.userDetail?.me?.userType || '');
    const userDetail = useSelector((state) => state.userDetail?.me || '');
    const storeId = useSelector((state) => state.widget.store?.id);
    const taffiStoreId = useSelector((state) => state.global.taffiStoreId);

    const signup = useMemo(
        () => (email, password) => {
            return auth.createUserWithEmailAndPassword(email, password);
        },
        [],
    );

    const login = useMemo(
        () => (email, password) => {
            return auth.signInWithEmailAndPassword(email, password);
        },
        [],
    );

    const loginWithGoogle = useMemo(
        () => () => {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });
            return auth.signInWithPopup(provider);
        },
        [],
    );

    const loginWithFacebook = useMemo(
        () => () => {
            const provider = new FacebookAuthProvider();
            return auth.signInWithPopup(provider);
        },
        [],
    );

    const loginWithApple = useMemo(
        () => () => {
            const provider = new OAuthProvider('apple.com');
            return auth.signInWithPopup(provider);
        },
        [],
    );

    const [findUser] = useLazyQuery(USER_DETAIL, {
        onCompleted(response) {
            if (response.me) {
                if (currentUser && response?.me?.id === currentUser.uid) {
                    loginCallBack({
                        currentUser,
                        history,
                        dispatch,
                        me: response.me,
                        setLoading,
                    });
                }
            } else {
                history.push(PATH.ADDITIONAL_SIGNUP_DETAILS, {
                    socialProviderInfo,
                });
            }
        },
        fetchPolicy: 'network-only',
        onError(error) {
            console.error('get data Failed: ', error);
        },
    });

    const [createUserIfNotExistMutation] = useMutation(CREATE_USER_IF_NOT_EXIST, {
        onCompleted(response) {
            onCreateUserIfNotExistCompleted(response);
        },
        onError(error) {
            if (error.message) {
                methods.setError('password', {
                    type: 'manual',
                    message: error && error.message,
                });
            }
        },
    });

    const [transformLocalUserDataMutation] = useMutation(TRANSFORM_LOCAL_USER_DATA);

    const socialLoginCallback = useMemo(
        () => async (user) => {
            let displayName = _split(user.displayName, ' ');
            let firstName = displayName[0] || '';
            let lastName = displayName.length > 1 ? displayName[displayName.length - 1] : '';
            const socialProviderInfo = {
                email: user.email,
                firstName: firstName,
                lastName: lastName,
                userType: UserType.Customer,
                id: user.uid,
            };
            setSocialProviderInfo(socialProviderInfo);
            const response = await findUser({
                variables: {
                    state: new Date(),
                },
            });
            // Handles update user profile with storeIds
            if (response?.data?.me?.userType === UserType.Customer) {
                updateProfileMutation({
                    variables: {
                        input: {
                            storeIds: utils?.isAppInWidgetMode() ? [storeId] : [taffiStoreId],
                        },
                    },
                });
            }
            await utils.transformLocalUser(transformLocalUserDataMutation);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [findUser, storeId, taffiStoreId],
    );

    const checkUserExistAuth = useMemo(
        () => (input) => {
            const variables = {
                input,
            };
            createUserIfNotExistMutation({ variables });
        },
        [createUserIfNotExistMutation],
    );

    const onCreateUserIfNotExistCompleted = async (response) => {
        try {
            if (response.createUserIfNotExist) {
                // force refresh token to get custom claims
                const currentUser = auth.currentUser;
                if (currentUser) {
                    await currentUser.getIdToken(true);
                }
                await utils.transformLocalUser(transformLocalUserDataMutation);
                const updatedUser = response.createUserIfNotExist;
                trackSignupEvent({
                    email: updatedUser.email,
                    userId: updatedUser.id,
                    userType: updatedUser.userType,
                    authMethod: currentUser?.providerData[0]?.providerId,
                });
                getMeFromServerWrap();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const [updateProfileMutation] = useMutation(UPDATE_PROFILE, {
        onError(error) {
            console.error('Update Profile Failed: ', error);
        },
    });

    const [getMeFromServer] = useLazyQuery(USER_DETAIL, {
        fetchPolicy: 'network-only',
        onError(error) {
            console.error('get data Failed: ', error);
        },
    });

    const logout = useMemo(
        () => () => {
            return auth.signOut();
        },
        [],
    );

    const handleLogout = useMemo(
        () =>
            async ({ isAdminAccount = false }) => {
                try {
                    localStorage.removeItem(LOCAL_STORAGE_KEY.LOCAL_USER_KEY);
                    await logout();
                    removeAuthToken();
                    history.push(PATH.LOGIN);
                    localStorage.clear();
                    await persistor.purge();
                    dispatch(authenticationActions.logout());
                    dispatch(authenticationActions.setAdminAccountAlert(isAdminAccount));
                    sessionStorage.setItem('isMobile', 'true');
                    localStorage.removeItem('questionInfo');
                    localStorage.removeItem('quizId');
                    localStorage.removeItem('ALGOLIA_TOKEN');
                    sessionStorage.removeItem('from');
                } catch {
                    toast.dark('logout failed');
                }
            },
        [dispatch, history, logout, removeAuthToken],
    );

    const [getSearchKeyFuc] = useLazyQuery(GET_SEARCH_KEY, {
        fetchPolicy: 'network-only',
        context: {
            scope: 'private',
            approved: true,
        },
    });

    const obtainAlgoliaAPIKey = async () => {
        try {
            const algoliaToken = localStorage.getItem('ALGOLIA_TOKEN');
            if (!algoliaToken) {
                const res = await getSearchKeyFuc();
                localStorage.setItem('ALGOLIA_TOKEN', res?.data?.getSearchKey?.token || '');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getMeFromServerWrap = useMemo(
        () => async () => {
            const { data: response } = await getMeFromServer();
            if (response?.me && currentUserRef.current) {
                if (response?.me?.userType === UserType.Customer) {
                    // Handles update user profile with storeIds
                    await updateProfileMutation({
                        variables: {
                            input: {
                                storeIds: utils?.isAppInWidgetMode() ? [storeId] : [taffiStoreId],
                            },
                        },
                    });
                }
                if (response?.me?.id === currentUserRef?.current?.toJSON().uid) {
                    await obtainAlgoliaAPIKey();
                    if (
                        location.pathname.includes('login') ||
                        location.pathname.includes('signup') ||
                        location.pathname.includes(PATH.ADDITIONAL_SIGNUP_DETAILS) ||
                        location.pathname.includes(PATH.SIGNUP_STYLIST)
                    ) {
                        loginCallBack({
                            currentUser: currentUserRef?.current,
                            history,
                            dispatch,
                            me: response?.me,
                            setLoading,
                            handleLogout,
                        });
                    }
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, getMeFromServer, handleLogout, history, location.pathname],
    );

    const resetPassword = useMemo(
        () => (email) => {
            return auth.sendPasswordResetEmail(email);
        },
        [],
    );

    const updateEmail = useMemo(
        () => (email) => {
            return currentUserRef.current.updateEmail(email);
        },
        [],
    );

    const updatePassword = useMemo(
        () => (password) => {
            return currentUserRef.current.updatePassword(password);
        },
        [],
    );
    const confirmPasswordReset = useMemo(
        () => (code, password) => {
            return auth.confirmPasswordReset(code, password);
        },
        [],
    );
    const checkActionCode = useMemo(
        () => (code) => {
            return auth.checkActionCode(code);
        },
        [],
    );

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                if (!currentUser || currentUser.uid !== user.uid) {
                    setIsUserLoggedIn(true);
                }
            } else if (currentUser) {
                refreshAnalyticId();
                trackEvent(TRACK_EVENT.LOGOUT, {
                    email: currentUser.email,
                    userId: currentUser.uid,
                    userType: userType,
                    authMethod: currentUser?.providerData[0]?.providerId,
                });
                setLoggedInUserType('');
                setLoggedInUserEmail('');
                setLoggedInUserGender('');
            }
            setCurrentUser(user);
            currentUserRef.current = user;
            setLoading(false);
        });

        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, userType]);

    useEffect(() => {
        if (userDetail) {
            setLoggedInUserType(userDetail.userType);
            setLoggedInUserEmail(userDetail.email);
            setLoggedInUserGender(userDetail.gender);
        }
    }, [userDetail]);

    useEffect(() => {
        if (isUserLoggedIn && loggedInUserType && currentUserRef.current) {
            const currentUser = currentUserRef.current.toJSON();
            let params = {
                email: currentUser.email || loggedInUserEmail,
                userId: currentUser.uid,
                userType: loggedInUserType,
                gender: loggedInUserGender, //gender will not available at signup. But it is confirmed as ok on ticket(WEBAPP-601)
                authMethod: currentUser?.providerData[0]?.providerId,
            };
            if (window?.Weglot?.getCurrentLang()) {
                params = {
                    ...params,
                    language: window?.Weglot?.getCurrentLang(),
                };
            }
            identify(currentUser.uid, params);
            setIsUserLoggedIn(false);
            setLoggedInUserType('');
            setLoggedInUserEmail('');
            setLoggedInUserGender('');
        }
    }, [isUserLoggedIn, loggedInUserType, loggedInUserEmail, loggedInUserGender]);

    const value = useMemo(
        () => ({
            currentUser,
            login,
            loginWithGoogle,
            loginWithFacebook,
            loginWithApple,
            socialLoginCallback,
            checkUserExistAuth,
            getMeFromServer: getMeFromServerWrap,
            signup,
            logout,
            resetPassword,
            updateEmail,
            updatePassword,
            confirmPasswordReset,
            checkActionCode,
            handleLogout,
        }),
        [
            checkActionCode,
            checkUserExistAuth,
            confirmPasswordReset,
            currentUser,
            getMeFromServerWrap,
            handleLogout,
            login,
            loginWithApple,
            loginWithFacebook,
            loginWithGoogle,
            logout,
            resetPassword,
            signup,
            socialLoginCallback,
            updateEmail,
            updatePassword,
        ],
    );

    return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
// AuthProvider.whyDidYouRender = true;
export default AuthProvider;
